/**
 * Componente: LoginForm
 * Formulário de autenticação com React 19 e validação Zod
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { loginSchema } from '@/lib/validations';
import { translateAuthError } from '@/lib/errorTranslations';
import { useAuthDebug } from '@/hooks/useAuthDebug';
import { useAuth } from '@/hooks/useAuth';
import { getDefaultRouteByRole } from '@/lib/roleRouting';

type LoginFormProps = {
  onSuccess?: () => void;
};

/** Aceita apenas paths relativos internos (anti open-redirect). */
function resolveSafeRedirect(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  const decoded = decodeURIComponent(raw).trim();
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return fallback;
  if (decoded.startsWith('/login')) return fallback;
  return decoded;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [displayedError, setDisplayedError] = useState<string>('');
  const [isPending, setIsPending] = useState(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isDebugMode } = useAuthDebug();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPending(true);
    setFieldErrors({});
    setDisplayedError('');

    const formData = new FormData(formRef.current!);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      // Validação local com Zod para feedback imediato
      const validationResult = loginSchema.safeParse({
        email,
        password,
      });

      if (!validationResult.success) {
        const errors = validationResult.error.flatten().fieldErrors;
        setFieldErrors(errors);
        setDisplayedError('Por favor, verifique os erros no formulário');
        setIsPending(false);
        return;
      }

      // Usa o login do useAuth que salva no localStorage E atualiza o estado React
      const session = await login(validationResult.data);

      onSuccess?.();

      const defaultRoute = getDefaultRouteByRole(session?.user?.role);
      const destination = resolveSafeRedirect(searchParams.get('redirect'), defaultRoute);

      // Redireciona após um pequeno delay para garantir que o estado foi atualizado
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }

      redirectTimeoutRef.current = setTimeout(() => {
        navigate(destination, { replace: true });
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Erro ao fazer login. Tente novamente.';
      if (error instanceof Error) {
        errorMessage = translateAuthError(error.message);
      }
      
      setDisplayedError(errorMessage);
      setIsPending(false);
    }
  };

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full">
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        {displayedError && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{displayedError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="seu@email.com"
            required
            disabled={isPending}
            className={`transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.email ? 'border-red-500' : 'border-gray-300'}`}
          />
          {fieldErrors.email && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.email?.[0]}</p>
          )}
        </div>


        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
              Senha
            </Label>
            <Link
              to="/forgot-password"
              className="text-xs text-teal-600 hover:text-teal-700 transition-colors"
            >
              Esqueceu?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Sua senha"
              required
              disabled={isPending}
              className={`pr-12 transition-colors focus:ring-2 focus:ring-teal-500 ${fieldErrors.password ? 'border-red-500' : 'border-gray-300'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 rounded transition-colors"
              disabled={isPending}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
          {fieldErrors.password && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.password?.[0]}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isPending}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
        >
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isPending ? 'Autenticando...' : 'Entrar'}
        </Button>

        <p className="text-sm text-gray-600 text-center">
          Não tem conta?{' '}
          <Link to="/register" className="text-teal-600 hover:text-teal-700 font-semibold">
            Registre-se
          </Link>
        </p>


        {/* Debug Information - Visible with ?debug=auth URL parameter */}
        {isDebugMode && (
          <div className="mt-6 p-3 bg-gray-900 text-gray-200 rounded-lg border border-gray-700 font-mono text-xs overflow-auto max-h-64">
            <p className="text-yellow-400 font-bold mb-2">🔍 DEBUG MODE</p>
            <p className="text-gray-500 mt-2 text-xs">Abra DevTools (F12) &gt; Console para mais logs</p>
          </div>
        )}
      </form>
    </div>
  );
}
